import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CategoryIcon from '@mui/icons-material/Category';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TicketIcon from '@mui/icons-material/LocalPlay';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, Grid, Typography, type BoxProps } from '@mui/material';

export type EmptyListIcon =
  | 'ERROR'
  | 'TICKET'
  | 'CARD_GIFT_CARD'
  | 'NO_POINT_HISTORY_ITEMS'
  | 'CATEGORY';

type EmptyListProps = {
  icon?: EmptyListIcon;
  message: string;
  'data-test-id'?: string;
  sx?: BoxProps['sx'];
};

function getEmptyListIconComponent(icon: EmptyListIcon) {
  switch (icon) {
    case 'ERROR':
      return ErrorOutlineIcon;
    case 'CARD_GIFT_CARD':
      return CardGiftcardIcon;
    case 'NO_POINT_HISTORY_ITEMS':
      return RestoreIcon;
    case 'CATEGORY':
      return CategoryIcon;
    default:
      return TicketIcon;
  }
}

/**
 * EmptyList
 *
 * Renders the empty list
 * @param message - the message to display
 * @param icon - the icon to display
 */
export function EmptyList({
  message,
  icon = 'CATEGORY',
  'data-test-id': dataTestId,
  sx,
}: EmptyListProps) {
  const IconComponent = getEmptyListIconComponent(icon);

  return (
    <Box
      data-test-id={dataTestId}
      sx={[
        (theme) => ({
          backgroundColor: theme.colors?.secondaryLight4,
          color: theme.colors?.secondaryDark,
          padding: theme.spacing(theme.gutters.md, 0, theme.gutters.md, 0),
          textAlign: 'center',
          width: '100%',
        }),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      <Grid container direction="column">
        <Grid item>
          <IconComponent
            data-test-id={`list-${icon}-icon-selector`}
            fontSize="large"
          />
        </Grid>
        <Grid item>
          <Typography
            data-test-id="empty-list-selector"
            variant="xsBold"
            sx={(theme) => ({
              color: theme.colors?.secondaryDark,
              textAlign: 'center',
              width: '100%',
            })}
          >
            {message}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
